/**
 * RTNU Shopify Cart Events
 * =========================
 * A theme-agnostic, drop-in JavaScript utility for Shopify cart event handling.
 * Works on ANY Shopify theme without modification - simply add the script tag.
 *
 * @version 1.0.0
 * @author RTNU
 * @license MIT
 *
 * INSTALLATION
 * ------------
 * Add to your theme.liquid or any layout file:
 *
 *   <script src="{{ 'shopify-cart-events.js' | asset_url }}" defer></script>
 *
 * The script automatically intercepts all cart operations and emits events.
 * No modifications to existing theme code required.
 *
 * EVENTS
 * ------
 * All events are emitted on `document` as CustomEvents and via the callback API.
 *
 * | Event Name           | Trigger                    | Detail Payload                                    |
 * |----------------------|----------------------------|---------------------------------------------------|
 * | cart:item-added      | Item added to cart         | { item, cart, quantity }                          |
 * | cart:item-removed    | Item removed from cart     | { item, cart }                                    |
 * | cart:item-updated    | Item quantity changed      | { item, cart, previousQuantity, newQuantity }     |
 * | cart:updated         | Any cart change            | { cart, changeType, changedItems }                |
 * | cart:cleared         | Cart emptied               | { previousCart }                                  |
 * | cart:error           | Cart operation failed      | { error, operation, data }                        |
 * | cart:bulk-start      | Bulk operation starting    | { operation, itemCount }                          |
 * | cart:bulk-complete   | Bulk operation finished    | { operation, results: { success, failed } }       |
 *
 * API REFERENCE
 * -------------
 *
 * const { CartEvents } = window.RTNU;
 *
 * // === EVENT LISTENERS ===
 * CartEvents.on('item-added', (detail) => { ... });     // Subscribe to event
 * CartEvents.once('item-added', (detail) => { ... });   // One-time listener
 * CartEvents.off('item-added', handler);                // Unsubscribe
 *
 * // === CART OPERATIONS (async, emit events automatically) ===
 * await CartEvents.add(variantId, quantity?, properties?);  // Add item to cart
 * await CartEvents.remove(lineItemKey);                     // Remove item by key
 * await CartEvents.update(updates);                         // Update items { key: qty }
 * await CartEvents.clear();                                 // Clear entire cart
 * const cart = await CartEvents.getCart(forceRefresh?);     // Get cart (cached or fresh)
 *
 * // === QUERY HELPERS (sync, use cached cart) ===
 * CartEvents.hasProduct(productId);        // Check if product in cart
 * CartEvents.hasProductType(type);         // Check if product type in cart
 * CartEvents.hasVariant(variantId);        // Check if variant in cart
 * CartEvents.hasVendor(vendor);            // Check if vendor in cart
 * CartEvents.findItems(filterFn);          // Find items matching predicate
 * CartEvents.getItemCount();               // Get total item count
 *
 * // === BULK OPERATIONS (async, queued with delay to avoid race conditions) ===
 * await CartEvents.removeItems(filterFn);              // Remove all matching items
 * await CartEvents.updateItems(filterFn, { quantity }); // Update all matching items
 *
 * // === STATE PROPERTIES (read-only) ===
 * CartEvents.cart;        // Current cached cart object
 * CartEvents.items;       // Shortcut to cart.items
 * CartEvents.itemCount;   // Shortcut to cart.item_count
 * CartEvents.totalPrice;  // Shortcut to cart.total_price
 * CartEvents.isBusy;      // True if bulk operation in progress
 *
 * USAGE EXAMPLES
 * --------------
 *
 * Example 1: React to items being added
 *
 *   const { CartEvents } = window.RTNU;
 *
 *   CartEvents.on('item-added', ({ item, cart }) => {
 *     console.log(`Added ${item.title} to cart`);
 *     console.log(`Cart now has ${cart.item_count} items`);
 *   });
 *
 * Example 2: Membership product unlocking member-only products
 *
 *   const { CartEvents } = window.RTNU;
 *   const MEMBERSHIP_PRODUCT_ID = 123456789;
 *
 *   // When membership is added, unlock member-only products on page
 *   CartEvents.on('item-added', ({ item }) => {
 *     if (item.product_id === MEMBERSHIP_PRODUCT_ID) {
 *       document.querySelectorAll('[data-member-only]').forEach(el => {
 *         el.classList.add('unlocked');
 *       });
 *     }
 *   });
 *
 *   // When membership is removed, remove all member-only products from cart
 *   CartEvents.on('item-removed', async ({ item }) => {
 *     if (item.product_id === MEMBERSHIP_PRODUCT_ID) {
 *       await CartEvents.removeItems(item => item.product_type === 'member-only');
 *       document.querySelectorAll('[data-member-only]').forEach(el => {
 *         el.classList.remove('unlocked');
 *       });
 *     }
 *   });
 *
 * Example 3: Show loading state during bulk operations
 *
 *   CartEvents.on('bulk-start', () => {
 *     document.body.classList.add('cart-updating');
 *   });
 *
 *   CartEvents.on('bulk-complete', ({ results }) => {
 *     document.body.classList.remove('cart-updating');
 *     console.log(`Processed ${results.success.length} items`);
 *   });
 *
 * Example 4: Using native DOM events instead of callback API
 *
 *   document.addEventListener('cart:item-added', (event) => {
 *     const { item, cart } = event.detail;
 *     console.log(`Added ${item.title}`);
 *   });
 *
 * Example 5: Check cart state synchronously
 *
 *   if (CartEvents.hasProductType('membership')) {
 *     showMemberPricing();
 *   }
 *
 *   const giftItems = CartEvents.findItems(item => item.properties?._gift === 'true');
 *
 */

(function () {
  'use strict';

  // Prevent double initialization
  if (window.RTNU?.CartEvents) {
    console.warn('[RTNU CartEvents] Already initialized');
    return;
  }

  // Create RTNU namespace
  window.RTNU = window.RTNU || {};

  // ============================================================================
  // CONFIGURATION
  // ============================================================================

  const CONFIG = {
    // Delay between bulk operations to avoid race conditions (ms)
    bulkOperationDelay: 100,

    // Cart API endpoints
    endpoints: {
      get: '/cart.js',
      add: '/cart/add.js',
      change: '/cart/change.js',
      update: '/cart/update.js',
      clear: '/cart/clear.js',
    },

    // Event prefix for DOM events
    eventPrefix: 'cart:',
  };

  // ============================================================================
  // STATE
  // ============================================================================

  let cachedCart = null;
  let isBusy = false;
  const listeners = new Map();

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  /**
   * Check if a URL is a cart endpoint
   */
  const isCartEndpoint = (url) => {
    const urlString = url instanceof Request ? url.url : url.toString();
    return Object.values(CONFIG.endpoints).some((endpoint) =>
      urlString.includes(endpoint)
    );
  };

  /**
   * Get the endpoint type from a URL
   */
  const getEndpointType = (url) => {
    const urlString = url instanceof Request ? url.url : url.toString();
    for (const [type, endpoint] of Object.entries(CONFIG.endpoints)) {
      if (urlString.includes(endpoint)) return type;
    }
    return null;
  };

  /**
   * Parse request body from various formats
   */
  const parseRequestBody = async (options) => {
    if (!options?.body) return null;

    const body = options.body;

    // Handle FormData
    if (body instanceof FormData) {
      const obj = {};
      body.forEach((value, key) => {
        obj[key] = value;
      });
      return obj;
    }

    // Handle URLSearchParams
    if (body instanceof URLSearchParams) {
      const obj = {};
      body.forEach((value, key) => {
        obj[key] = value;
      });
      return obj;
    }

    // Handle JSON string
    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch {
        // Try URL encoded
        const params = new URLSearchParams(body);
        const obj = {};
        params.forEach((value, key) => {
          obj[key] = value;
        });
        return obj;
      }
    }

    return body;
  };

  /**
   * Compare two cart states and find differences
   */
  const compareCartStates = (previousCart, newCart) => {
    const changes = {
      added: [],
      removed: [],
      updated: [],
    };

    if (!previousCart || !newCart) return changes;

    const previousItems = new Map(
      previousCart.items?.map((item) => [item.key, item]) || []
    );
    const newItems = new Map(
      newCart.items?.map((item) => [item.key, item]) || []
    );

    // Find added and updated items
    for (const [key, item] of newItems) {
      const previousItem = previousItems.get(key);
      if (!previousItem) {
        changes.added.push(item);
      } else if (previousItem.quantity !== item.quantity) {
        changes.updated.push({
          item,
          previousQuantity: previousItem.quantity,
          newQuantity: item.quantity,
        });
      }
    }

    // Find removed items
    for (const [key, item] of previousItems) {
      if (!newItems.has(key)) {
        changes.removed.push(item);
      }
    }

    return changes;
  };

  /**
   * Sleep for a specified duration
   */
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // ============================================================================
  // EVENT SYSTEM
  // ============================================================================

  /**
   * Emit an event via both callback system and DOM CustomEvent
   */
  const emit = (eventName, detail) => {
    // Emit via callback system
    const callbacks = listeners.get(eventName) || [];
    callbacks.forEach((callback) => {
      try {
        callback(detail);
      } catch (error) {
        console.error(`[RTNU CartEvents] Error in ${eventName} listener:`, error);
      }
    });

    // Emit via DOM CustomEvent
    const domEventName = CONFIG.eventPrefix + eventName;
    document.dispatchEvent(
      new CustomEvent(domEventName, {
        detail,
        bubbles: true,
        composed: true,
      })
    );
  };

  /**
   * Subscribe to an event
   */
  const on = (eventName, callback) => {
    if (!listeners.has(eventName)) {
      listeners.set(eventName, []);
    }
    listeners.get(eventName).push(callback);
  };

  /**
   * Subscribe to an event once
   */
  const once = (eventName, callback) => {
    const wrapper = (detail) => {
      off(eventName, wrapper);
      callback(detail);
    };
    on(eventName, wrapper);
  };

  /**
   * Unsubscribe from an event
   */
  const off = (eventName, callback) => {
    const callbacks = listeners.get(eventName);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  };

  // ============================================================================
  // FETCH INTERCEPTION
  // ============================================================================

  const originalFetch = window.fetch;

  window.fetch = async function (url, options = {}) {
    const response = await originalFetch.call(this, url, options);

    // Only process cart endpoints
    if (!isCartEndpoint(url)) {
      return response;
    }

    const endpointType = getEndpointType(url);
    const previousCart = cachedCart ? { ...cachedCart } : null;

    try {
      // Clone response to read it without consuming
      const clonedResponse = response.clone();
      const data = await clonedResponse.json();

      // Update cache (cart endpoints return the full cart)
      if (data && typeof data === 'object') {
        // /cart/add.js returns the added item, not the cart
        if (endpointType === 'add') {
          // Fetch fresh cart after add
          const cartResponse = await originalFetch(CONFIG.endpoints.get);
          cachedCart = await cartResponse.json();
        } else {
          cachedCart = data;
        }
      }

      // Parse request body for context
      const requestBody = await parseRequestBody(options);

      // Emit appropriate events based on endpoint
      switch (endpointType) {
        case 'add': {
          // data is the added item(s)
          const addedItems = Array.isArray(data.items) ? data.items : [data];
          addedItems.forEach((item) => {
            emit('item-added', {
              item,
              cart: cachedCart,
              quantity: item.quantity,
            });
          });
          emit('updated', {
            cart: cachedCart,
            changeType: 'add',
            changedItems: addedItems,
          });
          break;
        }

        case 'change': {
          // Compare carts to determine what changed
          const changes = compareCartStates(previousCart, cachedCart);

          changes.removed.forEach((item) => {
            emit('item-removed', { item, cart: cachedCart });
          });

          changes.updated.forEach(({ item, previousQuantity, newQuantity }) => {
            emit('item-updated', {
              item,
              cart: cachedCart,
              previousQuantity,
              newQuantity,
            });
          });

          const changedItems = [...changes.removed, ...changes.updated.map((u) => u.item)];
          if (changedItems.length > 0) {
            emit('updated', {
              cart: cachedCart,
              changeType: changes.removed.length > 0 ? 'remove' : 'update',
              changedItems,
            });
          }
          break;
        }

        case 'update': {
          // /cart/update.js can update multiple things
          const changes = compareCartStates(previousCart, cachedCart);

          changes.updated.forEach(({ item, previousQuantity, newQuantity }) => {
            emit('item-updated', {
              item,
              cart: cachedCart,
              previousQuantity,
              newQuantity,
            });
          });

          changes.removed.forEach((item) => {
            emit('item-removed', { item, cart: cachedCart });
          });

          emit('updated', {
            cart: cachedCart,
            changeType: 'update',
            changedItems: [
              ...changes.updated.map((u) => u.item),
              ...changes.removed,
            ],
          });
          break;
        }

        case 'clear': {
          emit('cleared', { previousCart });
          emit('updated', {
            cart: cachedCart,
            changeType: 'clear',
            changedItems: previousCart?.items || [],
          });
          break;
        }
      }
    } catch (error) {
      emit('error', {
        error,
        operation: endpointType,
        data: null,
      });
    }

    return response;
  };

  // ============================================================================
  // CART OPERATIONS
  // ============================================================================

  /**
   * Add an item to the cart
   */
  const add = async (variantId, quantity = 1, properties = {}) => {
    const response = await fetch(CONFIG.endpoints.add, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity,
        properties,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Add to cart failed' }));
      emit('error', { error, operation: 'add', data: { variantId, quantity, properties } });
      throw new Error(error.message || error.description || 'Add to cart failed');
    }

    return response.json();
  };

  /**
   * Remove an item from the cart by key
   */
  const remove = async (lineItemKey) => {
    return update({ [lineItemKey]: 0 });
  };

  /**
   * Update cart items
   * @param {Object} updates - { lineItemKey: quantity } or { line: index, quantity: qty }
   */
  const update = async (updates) => {
    // Handle { line, quantity } format
    if (updates.line !== undefined && updates.quantity !== undefined) {
      const response = await fetch(CONFIG.endpoints.change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Update failed' }));
        emit('error', { error, operation: 'update', data: updates });
        throw new Error(error.message || error.description || 'Update failed');
      }

      return response.json();
    }

    // Handle { key: quantity } format
    const response = await fetch(CONFIG.endpoints.update, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Update failed' }));
      emit('error', { error, operation: 'update', data: updates });
      throw new Error(error.message || error.description || 'Update failed');
    }

    return response.json();
  };

  /**
   * Clear the entire cart
   */
  const clear = async () => {
    const response = await fetch(CONFIG.endpoints.clear, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Clear cart failed' }));
      emit('error', { error, operation: 'clear', data: null });
      throw new Error(error.message || error.description || 'Clear cart failed');
    }

    return response.json();
  };

  /**
   * Get the current cart (cached or fresh)
   */
  const getCart = async (forceRefresh = false) => {
    if (!forceRefresh && cachedCart) {
      return cachedCart;
    }

    const response = await fetch(CONFIG.endpoints.get);
    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }

    cachedCart = await response.json();
    return cachedCart;
  };

  // ============================================================================
  // QUERY HELPERS
  // ============================================================================

  /**
   * Check if a product is in the cart
   */
  const hasProduct = (productId) => {
    return cachedCart?.items?.some((item) => item.product_id === productId) || false;
  };

  /**
   * Check if a product type is in the cart
   */
  const hasProductType = (type) => {
    return cachedCart?.items?.some((item) => item.product_type === type) || false;
  };

  /**
   * Check if a variant is in the cart
   */
  const hasVariant = (variantId) => {
    return cachedCart?.items?.some((item) => item.variant_id === variantId) || false;
  };

  /**
   * Check if a vendor is in the cart
   */
  const hasVendor = (vendor) => {
    return cachedCart?.items?.some((item) => item.vendor === vendor) || false;
  };

  /**
   * Find items matching a predicate function
   */
  const findItems = (filterFn) => {
    return cachedCart?.items?.filter(filterFn) || [];
  };

  /**
   * Get total item count
   */
  const getItemCount = () => {
    return cachedCart?.item_count || 0;
  };

  // ============================================================================
  // BULK OPERATIONS
  // ============================================================================

  /**
   * Remove all items matching a filter function
   */
  const removeItems = async (filterFn) => {
    const itemsToRemove = findItems(filterFn);

    if (itemsToRemove.length === 0) {
      return { success: [], failed: [] };
    }

    isBusy = true;
    emit('bulk-start', { operation: 'remove', itemCount: itemsToRemove.length });

    const results = { success: [], failed: [] };

    for (const item of itemsToRemove) {
      try {
        await remove(item.key);
        results.success.push(item);
      } catch (error) {
        results.failed.push({ item, error });
      }

      // Delay between operations to avoid race conditions
      if (itemsToRemove.indexOf(item) < itemsToRemove.length - 1) {
        await sleep(CONFIG.bulkOperationDelay);
      }
    }

    isBusy = false;
    emit('bulk-complete', { operation: 'remove', results });

    return results;
  };

  /**
   * Update all items matching a filter function
   */
  const updateItems = async (filterFn, updates) => {
    const itemsToUpdate = findItems(filterFn);

    if (itemsToUpdate.length === 0) {
      return { success: [], failed: [] };
    }

    isBusy = true;
    emit('bulk-start', { operation: 'update', itemCount: itemsToUpdate.length });

    const results = { success: [], failed: [] };

    for (const item of itemsToUpdate) {
      try {
        await update({ line: cachedCart.items.indexOf(item) + 1, ...updates });
        results.success.push(item);
      } catch (error) {
        results.failed.push({ item, error });
      }

      // Delay between operations
      if (itemsToUpdate.indexOf(item) < itemsToUpdate.length - 1) {
        await sleep(CONFIG.bulkOperationDelay);
      }
    }

    isBusy = false;
    emit('bulk-complete', { operation: 'update', results });

    return results;
  };

  // ============================================================================
  // CACHE INVALIDATION
  // ============================================================================

  /**
   * Invalidate cache on page visibility change (user returns to tab)
   */
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      // Refresh cart when user returns to tab
      getCart(true).catch(() => {
        // Silent fail - cart will refresh on next operation
      });
    }
  });

  /**
   * Invalidate cache on page show (back/forward navigation)
   */
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      // Page was restored from bfcache
      getCart(true).catch(() => {
        // Silent fail
      });
    }
  });

  // ============================================================================
  // INITIAL CART FETCH
  // ============================================================================

  /**
   * Fetch cart on initialization
   */
  getCart(true).catch(() => {
    console.warn('[RTNU CartEvents] Failed to fetch initial cart state');
  });

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  window.RTNU.CartEvents = {
    // Event methods
    on,
    once,
    off,

    // Cart operations
    add,
    remove,
    update,
    clear,
    getCart,

    // Query helpers
    hasProduct,
    hasProductType,
    hasVariant,
    hasVendor,
    findItems,
    getItemCount,

    // Bulk operations
    removeItems,
    updateItems,

    // State properties (getters for read-only access)
    get cart() {
      return cachedCart;
    },
    get items() {
      return cachedCart?.items || [];
    },
    get itemCount() {
      return cachedCart?.item_count || 0;
    },
    get totalPrice() {
      return cachedCart?.total_price || 0;
    },
    get isBusy() {
      return isBusy;
    },
  };

  console.log('[RTNU CartEvents] Initialized');
})();
